import axios from "axios";

// Points at your Supabase project's Edge Functions, e.g.
// https://YOUR_PROJECT_REF.supabase.co/functions/v1
const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Kept in case anything imports it elsewhere.
export const API_BASE = FUNCTIONS_URL;

export const api = axios.create({ baseURL: FUNCTIONS_URL });

// Maps an old FastAPI-style path (e.g. "/restaurant/dashboard",
// "/customer/orders", "/restaurants/spice-route/menu") to the Edge
// Function that now serves it (e.g. "/restaurant-api/restaurant/dashboard").
// This is the ONLY place that needed to change — every page in the app
// still calls api.get("/restaurant/dashboard") etc. exactly as before.
function toFunctionPath(originalUrl) {
  const [cleanPath, ...qParts] = originalUrl.split("?");
  const query = qParts.length ? "?" + qParts.join("?") : "";
  const segs = cleanPath.replace(/^\//, "").split("/").filter(Boolean);

  const build = (fn, restSegs) => `/${fn}/${restSegs.join("/")}${query}`.replace(/\/$/, "") + query;

  if (segs.length === 0) return `/public-api${query}`;

  if (segs[0] === "customer") {
    if (["register", "login", "me"].includes(segs[1])) return build("customer-auth", [segs[1]]);
    if (segs[1] === "orders") return build("orders-api", ["customer", "orders"]);
    if (segs[1] === "my-reviews") return build("public-api", ["customer", "my-reviews"]);
    if (segs[1] === "loyalty") return build("public-api", ["customer", "loyalty"]);
  }

  if (segs[0] === "restaurant") {
    if (["register", "login", "me"].includes(segs[1])) return build("restaurant-auth", [segs[1]]);
    if (segs[1] === "orders") return build("orders-api", ["restaurant", "orders", ...segs.slice(2)]);
    return build("restaurant-api", ["restaurant", ...segs.slice(1)]);
  }

  if (segs[0] === "admin") {
    if (segs[1] === "login") return build("admin-auth", ["login"]);
    if (segs[1] === "change-password") return build("admin-auth", ["change-password"]);
    return build("admin-api", ["admin", ...segs.slice(1)]);
  }

  if (segs[0] === "restaurants") return build("public-api", ["restaurants", ...segs.slice(1)]);
  if (segs[0] === "tables") return build("public-api", ["tables", ...segs.slice(1)]);
  if (segs[0] === "tap") return build("public-api", ["tap"]);
  if (segs[0] === "demo-bookings") return build("public-api", ["demo-bookings"]);
  if (segs[0] === "orders") return build("orders-api", ["orders", ...segs.slice(1)]);
  if (segs[0] === "uploads") return build("uploads-api", ["uploads"]);

  return originalUrl; // fallback: leave unchanged
}

// Token + routing interceptor
api.interceptors.request.use((config) => {
  const originalUrl = config.url || "";

  // Same token-selection rules as before, based on the ORIGINAL path.
  let token = null;
  if (originalUrl.includes("/admin/") && !originalUrl.includes("/admin/login")) {
    token = localStorage.getItem("tabdine_admin_session");
  } else if (originalUrl.startsWith("/restaurant/") && !originalUrl.includes("/restaurant/login")) {
    token = localStorage.getItem("tabdine_restaurant_session");
  } else if (
    originalUrl.startsWith("/customer/") ||
    originalUrl.startsWith("/orders") ||
    (originalUrl.match(/\/restaurants\/[^/]+\/reviews$/) && config.method === "post")
  ) {
    token = localStorage.getItem("tabdine_customer_session");
  } else {
    token = localStorage.getItem("tabdine_customer_session");
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Supabase's gateway wants this on every Edge Function call.
  if (ANON_KEY) config.headers.apikey = ANON_KEY;

  config.url = toFunctionPath(originalUrl);
  return config;
});

// ------- session helpers (unchanged) -------
export const customerSession = {
  get: () => {
    const t = localStorage.getItem("tabdine_customer_session");
    const u = localStorage.getItem("tabdine_customer_user");
    return t ? { token: t, user: u ? JSON.parse(u) : null } : null;
  },
  set: (token, user) => {
    localStorage.setItem("tabdine_customer_session", token);
    localStorage.setItem("tabdine_customer_user", JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem("tabdine_customer_session");
    localStorage.removeItem("tabdine_customer_user");
  },
};

export const restaurantSession = {
  get: () => {
    const t = localStorage.getItem("tabdine_restaurant_session");
    const r = localStorage.getItem("tabdine_restaurant_user");
    return t ? { token: t, restaurant: r ? JSON.parse(r) : null } : null;
  },
  set: (token, restaurant) => {
    localStorage.setItem("tabdine_restaurant_session", token);
    localStorage.setItem("tabdine_restaurant_user", JSON.stringify(restaurant));
  },
  clear: () => {
    localStorage.removeItem("tabdine_restaurant_session");
    localStorage.removeItem("tabdine_restaurant_user");
  },
};

export const adminSession = {
  get: () => localStorage.getItem("tabdine_admin_session"),
  set: (t) => localStorage.setItem("tabdine_admin_session", t),
  clear: () => localStorage.removeItem("tabdine_admin_session"),
};

// ---- cart helpers (per restaurant, unchanged) ----
export const cartKey = (slug) => `tabdine_cart_${slug}`;
export const getCart = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(cartKey(slug)) || "[]");
  } catch {
    return [];
  }
};
export const setCart = (slug, items) => {
  localStorage.setItem(cartKey(slug), JSON.stringify(items));
};
export const clearCart = (slug) => localStorage.removeItem(cartKey(slug));

// Media URL helper — uploads-api now returns full Supabase Storage public
// URLs directly, so this mostly just passes those through unchanged.
export const mediaUrl = (u) => {
  if (!u) return "";
  return u;
};

// Currency
export const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
